import { Pipe, PipeTransform } from '@angular/core'
import { AuthService, ClinicService, FacilityService } from '@mmx/shared'
import { isDate } from 'lodash'
import moment from 'moment-timezone'

type DateLike = Date | moment.Moment | string | number

// todo revisit isValidDate and these pipes: get rid of code duplicaiton, add unit tests, etc

function isValidDate(date: DateLike) {
  return (
    date != null &&
    date !== '' &&
    (isDate(date) ||
      (moment.isMoment(date) && date.isValid()) ||
      typeof date === 'string' ||
      typeof date === 'number')
  )
}

/**
 * momentDate pipe
 *
 * Uses moment to format dates, by default it will formats the date in the user's preferred date format.
 * You can optional specify the timezone and a desired format.
 */
@Pipe({ name: 'momentDate', pure: false, standalone: false })
export class MomentDatePipe implements PipeTransform {
  constructor(
    private authService: AuthService,
    private clinicService: ClinicService,
    private facilityService: FacilityService,
  ) {}

  get defaultTimezone() {
    const facility = this.facilityService.current$.value
    return facility?.timezone || this.clinicService.clinic?.settings?.defaultTimezone || 'America/New_York'
  }

  /**
   * Use as {{ date | momentDate }} to default to current facility timezone.
   * Can specify timezone as {{ date | momentDate: timezone }}.
   * Can also use user's local timezone as {{ date | momentDate: 'local' }}
   * Optionally specify the format as {{ date | momentDate: 'local' : 'LL' }}
   */
  transform(dt?: DateLike, tz?: string, format?: string) {
    if (isValidDate(dt)) {
      if (!format) {
        format = this.authService.user?.settings.dateFormat || 'L'
      }

      let mom: moment.Moment

      if (tz === 'local') {
        mom = moment.utc(dt).local()
      } else {
        mom = moment.tz(dt, tz || this.defaultTimezone)
      }

      if (mom.isValid()) {
        return mom.format(format || 'L')
      }
    }

    return ''
  }
}

@Pipe({ name: 'momentTime', pure: false, standalone: false })
export class MomentTimePipe implements PipeTransform {
  constructor(private authService: AuthService) {}

  transform(dt?: DateLike, tz?: string, format?: string) {
    if (isValidDate(dt)) {
      if (!format && this.authService.user) {
        format = this.authService.user.settings.timeFormat
      }

      let mom: moment.Moment
      if (tz === 'local') {
        mom = moment.utc(dt).local()
      } else {
        mom = moment.tz(dt, tz)
      }

      if (mom.isValid()) {
        return mom.format(format || 'LT')
      }
    }

    return ''
  }
}

@Pipe({ name: 'momentDateTime', pure: false, standalone: false })
export class MomentDateTimePipe implements PipeTransform {
  constructor(
    private authService: AuthService,
    private clinicService: ClinicService,
    private facilityService: FacilityService,
  ) { }

  get defaultTimezone() {
    const facility = this.facilityService.current$.value
    return facility?.timezone || this.clinicService.clinic?.settings?.defaultTimezone || 'America/New_York'
  }

  transform(dt?: DateLike, tz?: string, format?: string) {
    if (isValidDate(dt)) {
      if (!format && this.authService.user) {
        const dFormat = this.authService.user.settings.dateFormat || 'L'
        const tFormat = this.authService.user.settings.timeFormat || 'LT'
        format = `${dFormat} ${tFormat}`
      }

      let mom: moment.Moment
      if (tz === 'local') {
        mom = moment.utc(dt).local()
      } else {
        mom = moment.tz(dt, tz || this.defaultTimezone)
      }

      if (mom.isValid()) {
        return mom.format(format || 'L LT')
      }
    }

    return ''
  }
}
